import { CompanyUser } from '@/api';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import { addOrUpdateCompanyUsersCache } from '../queries/company-cache';

export type TeamRole = 'admin' | 'guardian';

type UseUpdateTeamRoleActionProps = {
    user: CompanyUser;
    teamId: string;
};

type UseUpdateTeamRoleActionData = {
    /** Whether this viewer can change the member's roles in this team. */
    isAllowed: boolean;
    /** Why not, ready to show in a tooltip. Empty while allowed. */
    reason: string;
    updateTeamRole: (roles: TeamRole[]) => Promise<void>;
    loading: boolean;
    error: string;
};

/**
 * Changing a member's roles within one team: whether this viewer may do it, why
 * not when they may not, and the call itself. Kept together so a caller can
 * wire up a button — disabled state, tooltip, spinner and click — from one
 * hook.
 */
export function useUpdateTeamRoleAction(
    props: UseUpdateTeamRoleActionProps
): UseUpdateTeamRoleActionData {
    const { user, teamId } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // Team capabilities hang off the member's entry for that team.
    const canManageRoles = user.teams.find((team) => team.teamId === teamId)
        ?.capabilities.canManageRoles;
    const [isAllowed, reason] = useMemo(() => {
        // No entry means the member is not in this team at all, so there are no
        // roles to change.
        if (!canManageRoles) {
            return [false, t('actions.update_team_role.reason_not_in_team')];
        }

        // The API decides who is permitted to change roles in this team, and
        // hands back a ready-to-show reason when they are not.
        if (!canManageRoles.allowed) {
            return [
                false,
                canManageRoles.reason ??
                    t('actions.update_team_role.reason_not_permitted')
            ];
        }

        return [true, ''];
    }, [canManageRoles, t]);

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error `PATCH /v3/users/{id}/teams/{teamId}/roles`
    // declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'USER_NOT_IN_TEAM')) {
                return t('actions.update_team_role.error_not_in_team');
            }
            if (isApiError(error, 'COMPANY_USER_NOT_FOUND')) {
                return t('actions.update_team_role.error_not_found');
            }
            if (isApiError(error, 'FORBIDDEN')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (roles: TeamRole[]) =>
            openApiRef.current.users
                .updateUserTeamRoles(user.id, teamId, { roles })
                .then((response) => response.data.data.user),
        onSuccess: (updated) => {
            addOrUpdateCompanyUsersCache(queryClient, updated);

            UIToast.success(t('actions.update_team_role.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        isAllowed,
        reason,
        updateTeamRole: (roles: TeamRole[]) =>
            mutation.mutateAsync(roles).then(() => {}),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
